import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Calendar, Plus, X, Check, ArrowsClockwise, TrendUp, TrendDown, Equals,
  Stop, ClipboardText, Copy, PencilSimple,
} from "@phosphor-icons/react";
import {
  listEpisodes, getEpisode, startEpisode, addEpisodeDay, endEpisode,
  reopenEpisode, deleteEpisode,
} from "@/lib/api";
import {
  activeEpisodeDayCount, buildEpisodeSummary, buildEpisodeNarrative,
  buildCarryForward, severityFromTrend, relativeTime, fmtDayLabel, dayTrend,
} from "@/lib/episodes";
import { classifyConditionType, getTrackerForIssue, HELPED_OPTIONS } from "@/lib/symptomDiary";
import { ChipSelector, ConditionTrackerSection } from "@/components/SymptomFormSections";
import { showToast } from "@/lib/toast";

const TREND_OPTIONS = [
  { id: "better", label: "Better", icon: TrendDown, color: "#1A8C52" },
  { id: "same",   label: "Same",   icon: Equals,    color: "#64748B" },
  { id: "worse",  label: "Worse",  icon: TrendUp,   color: "#C72E2E" },
];

function severityColor(sev) {
  if (sev >= 7) return "#C72E2E";
  if (sev >= 4) return "#D78D1A";
  if (sev >= 1) return "#1A8C52";
  return "#94A3B8";
}

// ===================================================================== ACTIVE CARD

function EpisodeCard({ episode, issues, onUpdate, onEnd, onOpen }) {
  const issue = issues.find((i) => i.id === episode.linkedIssueId);
  const conditionLabel = episode.symptom
    || issue?.claimedAs
    || episode.suspectedCondition
    || "Ongoing episode";
  const dayCount = activeEpisodeDayCount(episode);
  const sideLabel = episode.bodySide && episode.bodySide !== "N/A" ? ` — ${episode.bodySide}` : "";
  return (
    <div className="episode-card" data-testid={`episode-card-${episode.id}`}>
      <div className="episode-card-head">
        <div className="episode-card-title">
          <Calendar size={14} weight="bold" />
          <span>Active Episode: {conditionLabel}{sideLabel} — Day {dayCount}</span>
        </div>
        <div className="episode-card-meta mono">
          last update: {relativeTime(episode.lastUpdate || episode.startDate)}
        </div>
      </div>
      <div className="episode-card-stats">
        <Stat label="DAYS" value={dayCount} testId={`ep-days-${episode.id}`} />
        <Stat
          label="LATEST"
          value={`${episode.latestSeverity ?? 0}/10`}
          color={severityColor(episode.latestSeverity)}
          testId={`ep-latest-${episode.id}`}
        />
        <Stat
          label="WORST"
          value={`${episode.worstSeverity ?? 0}/10`}
          color={severityColor(episode.worstSeverity)}
          testId={`ep-worst-${episode.id}`}
        />
        <Stat label="ENTRIES" value={episode.dayCount ?? 0} testId={`ep-count-${episode.id}`} />
      </div>
      <div className="episode-card-actions">
        <button
          className="btn btn--accent"
          onClick={() => onUpdate(episode, null)}
          data-testid={`ep-update-${episode.id}`}
        >
          <Plus size={12} weight="bold" /> UPDATE TODAY
        </button>
        <button
          className="btn btn--outline"
          onClick={() => onUpdate(episode, "better")}
          data-testid={`ep-better-${episode.id}`}
        >
          <TrendDown size={12} weight="bold" /> BETTER
        </button>
        <button
          className="btn btn--outline"
          onClick={() => onUpdate(episode, "same")}
          data-testid={`ep-same-${episode.id}`}
        >
          <Equals size={12} weight="bold" /> SAME
        </button>
        <button
          className="btn btn--outline"
          onClick={() => onUpdate(episode, "worse")}
          data-testid={`ep-worse-${episode.id}`}
        >
          <TrendUp size={12} weight="bold" /> WORSE
        </button>
        <button
          className="btn btn--outline"
          onClick={() => onOpen(episode)}
          data-testid={`ep-open-${episode.id}`}
        >
          <PencilSimple size={12} weight="bold" /> TIMELINE
        </button>
        <button
          className="btn btn--outline"
          onClick={() => onEnd(episode)}
          data-testid={`ep-end-${episode.id}`}
          style={{ marginLeft: "auto" }}
        >
          <Stop size={12} weight="bold" /> END EPISODE
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value, color, testId }) {
  return (
    <div className="episode-stat" data-testid={testId}>
      <div className="episode-stat-label">{label}</div>
      <div className="episode-stat-value" style={color ? { color } : null}>{value}</div>
    </div>
  );
}

// ===================================================================== UPDATE MODAL

function UpdateModal({ episode, days, prevDay, initialTrend, onCancel, onSaved }) {
  const carry = useMemo(() => buildCarryForward(prevDay), [prevDay]);
  const [trend, setTrend] = useState(initialTrend || "");
  const [severity, setSeverity] = useState(
    initialTrend ? severityFromTrend(carry.severity, initialTrend) : carry.severity,
  );
  const [medsTaken, setMedsTaken] = useState(carry.medsTaken);
  const [treatments, setTreatments] = useState(carry.treatments);
  const [impact, setImpact] = useState(carry.impact);
  const [missedActivities, setMissedActivities] = useState(carry.missedActivities);
  const [missedDays, setMissedDays] = useState("");
  const [treatmentHelped, setTreatmentHelped] = useState(carry.treatmentHelped);
  const [notes, setNotes] = useState("");
  const [conditionFields, setConditionFields] = useState(carry.conditionFields);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const tracker = useMemo(() => getTrackerForIssue(
    episode.linkedIssueId ? { claimedAs: episode.symptom, id: episode.linkedIssueId } : (episode.suspectedCondition || episode.symptom || ""),
  ), [episode]);

  function trendClick(id) {
    setTrend(id);
    setSeverity(severityFromTrend(carry.severity, id));
  }

  function copyYesterday() {
    setSeverity(carry.severity);
    setMedsTaken(carry.medsTaken);
    setTreatments(carry.treatments);
    setImpact(carry.impact);
    setMissedActivities(carry.missedActivities);
    setTreatmentHelped(carry.treatmentHelped);
    setConditionFields(carry.conditionFields);
    showToast({ variant: "success", message: "Pre-filled with yesterday's details.", duration: 2500 });
  }

  function toggle(list, setter) {
    return (v) => setter(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  }

  async function submit() {
    setBusy(true); setErr("");
    try {
      const payload = {
        date: new Date().toISOString(),
        trend: trend || undefined,
        severity,
        medsTaken,
        treatments,
        impact,
        missedActivities,
        missedDays: missedDays === "" ? null : Number(missedDays),
        treatmentHelped,
        conditionFields,
        notes,
      };
      const { day } = await addEpisodeDay(episode.id, payload);
      onSaved(day);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  }

  const dayNumber = days.length + 1;

  return (
    <div className="modal-backdrop" data-testid="episode-update-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="modal-panel modal-panel--lg">
        <div className="modal-header">
          <Calendar size={16} weight="bold" />
          <span>UPDATE EPISODE — DAY {dayNumber}</span>
          <button className="modal-close" onClick={onCancel}><X size={14} weight="bold" /></button>
        </div>
        <div className="modal-body modal-body--scroll">
          {prevDay && (
            <button
              type="button"
              className="btn btn--outline"
              style={{ marginBottom: 12 }}
              onClick={copyYesterday}
              data-testid="episode-same-as-yesterday"
            >
              <Copy size={12} weight="bold" /> SAME AS YESTERDAY (FILL FROM DAY {prevDay.episodeDayNumber || days.length})
            </button>
          )}

          <div className="form-grid">
            <div className="field field--full">
              <span className="field-label">HOW IS IT TODAY?</span>
              <div className="appt-link-chips" data-testid="episode-trend-chips">
                {TREND_OPTIONS.map((t) => {
                  const Icon = t.icon;
                  const active = trend === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      className={`appt-link-chip ${active ? "appt-link-chip--active" : ""}`}
                      style={active ? { background: `${t.color}1A`, borderColor: t.color, color: t.color } : null}
                      onClick={() => trendClick(t.id)}
                      data-testid={`episode-trend-${t.id}`}
                    >
                      <Icon size={11} weight="bold" /> <span>{t.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="field field--full">
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span className="field-label">SEVERITY TODAY</span>
                <span style={{ fontWeight: 800, fontSize: 16, color: severityColor(severity), fontFamily: "JetBrains Mono, monospace" }}>{severity}/10</span>
              </div>
              <input
                type="range" min="0" max="10" step="1" value={severity}
                onChange={(e) => setSeverity(Number(e.target.value))}
                data-testid="episode-severity"
                style={{ width: "100%", accentColor: severityColor(severity) }}
              />
            </div>

            {tracker && (
              <ConditionTrackerSection
                form={{ conditionFields }}
                patch={(_, v) => setConditionFields(v)}
                tracker={tracker}
              />
            )}

            <label className="field field--full">
              <span className="field-label">MEDICATION / TREATMENT USED TODAY</span>
              {medsTaken.length > 0 && (
                <div style={{ marginBottom: 6 }} data-testid="episode-meds-taken">
                  {medsTaken.map((m, i) => (
                    <div key={`${m.prescriptionId || m.name}-${i}`} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, flex: 1 }}>{m.name}</span>
                      <select
                        className="field-input"
                        style={{ width: 150 }}
                        value={m.helped || ""}
                        onChange={(e) => setMedsTaken(medsTaken.map((x, idx) => (idx === i ? { ...x, helped: e.target.value } : x)))}
                        data-testid={`episode-med-helped-${i}`}
                      >
                        <option value="">Did it help?</option>
                        {HELPED_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                      <button type="button" className="icon-btn" onClick={() => setMedsTaken(medsTaken.filter((_, idx) => idx !== i))}>
                        <X size={12} weight="bold" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <input
                className="field-input"
                placeholder="Add medication and press Enter..."
                onKeyDown={(e) => {
                  if (e.key === "Enter" && e.target.value.trim()) {
                    e.preventDefault();
                    setMedsTaken([...medsTaken, { prescriptionId: "", name: e.target.value.trim(), helped: "" }]);
                    e.target.value = "";
                  }
                }}
                data-testid="episode-custom-med"
              />
            </label>

            <div className="field field--full">
              <span className="field-label">FUNCTIONAL IMPACT TODAY</span>
              <ChipSelector
                options={Array.from(new Set([...impact, "Missed work", "Missed PT", "Missed duty",
                  "Could not stand > 30 min", "Could not walk normal distance",
                  "Could not sleep", "Could not concentrate", "Avoided people",
                  "Needed help", "Could not drive", "Stayed in bed"]))}
                value={impact}
                onToggle={toggle(impact, setImpact)}
                testId="episode-impact"
              />
            </div>

            <label className="field">
              <span className="field-label">MISSED WORK / PT / DUTY TODAY (DAYS)</span>
              <input
                type="number" min="0" step="0.5"
                className="field-input"
                value={missedDays}
                onChange={(e) => setMissedDays(e.target.value)}
                placeholder="e.g., 1"
                data-testid="episode-missed-days"
              />
            </label>

            <label className="field">
              <span className="field-label">OVERALL — DID TREATMENT HELP?</span>
              <select
                className="field-input"
                value={treatmentHelped}
                onChange={(e) => setTreatmentHelped(e.target.value)}
                data-testid="episode-treatment-helped"
              >
                <option value="">—</option>
                <option value="Helped">Helped</option>
                <option value="Somewhat helped">Somewhat helped</option>
                <option value="Did not help">Did not help</option>
                <option value="Not sure">Not sure</option>
              </select>
            </label>

            <label className="field field--full">
              <span className="field-label">NOTES (OPTIONAL)</span>
              <textarea
                className="field-input"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Anything important about today..."
                data-testid="episode-notes"
              />
            </label>
          </div>

          {err && <div className="form-report form-report--err" data-testid="episode-update-error">{err}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn--outline" onClick={onCancel} disabled={busy}>CANCEL</button>
          <button className="btn btn--accent" onClick={submit} disabled={busy} data-testid="episode-update-save">
            <Check size={14} weight="bold" /> {busy ? "Saving..." : `SAVE DAY ${dayNumber}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===================================================================== TIMELINE MODAL

function TimelineModal({ episode, days, onClose, onEdit, onPromoteEnded, isEnded }) {
  return (
    <div className="modal-backdrop" data-testid="episode-timeline-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-panel modal-panel--lg">
        <div className="modal-header">
          <Calendar size={16} weight="bold" />
          <span>{episode.symptom || "Episode"} — Timeline</span>
          <button className="modal-close" onClick={onClose}><X size={14} weight="bold" /></button>
        </div>
        <div className="modal-body modal-body--scroll">
          {days.length === 0 && <div className="empty-state">No daily entries yet.</div>}
          <div className="episode-timeline" data-testid="episode-timeline">
            {days.map((d, idx) => {
              const trend = dayTrend(d);
              return (
                <div key={d.id} className="episode-day-row" data-testid={`episode-day-${d.id}`}>
                  <div className="episode-day-rail">
                    <div className="episode-day-bullet" style={{ background: severityColor(d.severity) }} />
                    {idx < days.length - 1 && <div className="episode-day-line" />}
                  </div>
                  <div className="episode-day-body">
                    <div className="episode-day-head">
                      <span className="episode-day-num mono">Day {d.episodeDayNumber || idx + 1}</span>
                      <span className="episode-day-date">{fmtDayLabel(d.date)}</span>
                      <span className="episode-day-sev mono" style={{ color: severityColor(d.severity) }}>{d.severity}/10</span>
                      {trend && <span className={`episode-day-trend episode-day-trend--${trend}`}>{trend}</span>}
                      <button
                        className="icon-btn"
                        onClick={() => onEdit(d)}
                        data-testid={`episode-day-edit-${d.id}`}
                        title="Edit this day"
                        style={{ marginLeft: "auto" }}
                      >
                        <PencilSimple size={12} weight="bold" />
                      </button>
                    </div>
                    {(d.medsTaken || []).length > 0 && (
                      <div className="episode-day-line-item">
                        <strong>Meds:</strong> {d.medsTaken.map((m) => m.name).join(", ")}
                      </div>
                    )}
                    {(d.treatments || []).length > 0 && (
                      <div className="episode-day-line-item">
                        <strong>Treatment:</strong> {d.treatments.join(", ")}
                      </div>
                    )}
                    {(d.impact || []).length > 0 && (
                      <div className="episode-day-line-item">
                        <strong>Impact:</strong> {d.impact.join(", ")}
                      </div>
                    )}
                    {d.notes && <div className="episode-day-line-item">{d.notes}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="modal-footer">
          {isEnded && (
            <button className="btn btn--outline" onClick={onPromoteEnded} data-testid="episode-reopen-btn">
              <ArrowsClockwise size={12} weight="bold" /> REOPEN EPISODE
            </button>
          )}
          <button className="btn btn--outline" onClick={onClose}>CLOSE</button>
        </div>
      </div>
    </div>
  );
}

// ===================================================================== END / SUMMARY MODAL

function EndSummaryModal({ episode, days, onCancel, onConfirm, readonly = false }) {
  const summary = useMemo(() => buildEpisodeSummary(episode, days), [episode, days]);
  const narrative = useMemo(() => buildEpisodeNarrative(summary), [summary]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  function copyNarrative() {
    navigator.clipboard.writeText(narrative).then(() => {
      showToast({ variant: "success", message: "Copied to clipboard.", duration: 2000 });
    }).catch(() => {});
  }

  async function confirm() {
    setBusy(true);
    try {
      await onConfirm(notes);
    } finally { setBusy(false); }
  }

  return (
    <div className="modal-backdrop" data-testid="episode-end-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="modal-panel">
        <div className="modal-header">
          <ClipboardText size={16} weight="bold" />
          <span>{readonly ? "EPISODE SUMMARY" : "END EPISODE — SUMMARY"}</span>
          <button className="modal-close" onClick={onCancel}><X size={14} weight="bold" /></button>
        </div>
        <div className="modal-body modal-body--scroll">
          <div className="episode-summary-grid">
            <Stat label="DAYS" value={summary.totalDays} testId="summary-days" />
            <Stat label="ENTRIES" value={summary.dayCount} testId="summary-entries" />
            <Stat label="WORST" value={`${summary.worstSeverity}/10`} color={severityColor(summary.worstSeverity)} testId="summary-worst" />
            <Stat label="AVG" value={summary.avgSeverity} color={severityColor(summary.avgSeverity)} testId="summary-avg" />
            {summary.missedDaysTotal > 0 && (
              <Stat label="MISSED" value={`${summary.missedDaysTotal}d`} testId="summary-missed" />
            )}
          </div>

          <div className="episode-summary-narrative" data-testid="episode-summary-narrative">
            {narrative}
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="btn btn--outline" onClick={copyNarrative} data-testid="episode-copy-narrative">
              <Copy size={12} weight="bold" /> COPY TO CLIPBOARD
            </button>
          </div>

          {summary.medications.length > 0 && (
            <div className="episode-summary-row"><strong>Medications:</strong> {summary.medications.join(", ")}</div>
          )}
          {summary.treatments.length > 0 && (
            <div className="episode-summary-row"><strong>Treatments:</strong> {summary.treatments.join(", ")}</div>
          )}
          {summary.triggers.length > 0 && (
            <div className="episode-summary-row"><strong>Triggers:</strong> {summary.triggers.join(", ")}</div>
          )}
          {summary.impact.length > 0 && (
            <div className="episode-summary-row"><strong>Functional impact:</strong> {summary.impact.join(", ")}</div>
          )}

          {!readonly && (
            <label className="field field--full" style={{ marginTop: 12 }}>
              <span className="field-label">CLOSING NOTES (OPTIONAL)</span>
              <textarea
                className="field-input"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Anything wrap-up worth recording?"
                data-testid="episode-end-notes"
              />
            </label>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn--outline" onClick={onCancel}>CLOSE</button>
          {!readonly && (
            <button className="btn btn--accent" onClick={confirm} disabled={busy} data-testid="episode-end-confirm">
              <Check size={14} weight="bold" /> {busy ? "Ending..." : "END EPISODE"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ===================================================================== PANEL

export default function EpisodesPanel({ issues = [], onStart, onListChanged }) {
  const [active, setActive] = useState([]);
  const [ended, setEnded] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("active"); // "active" | "ended"

  const [updateState, setUpdateState] = useState(null); // { episode, days, prevDay, initialTrend }
  const [endState, setEndState] = useState(null); // { episode, days }
  const [timelineState, setTimelineState] = useState(null); // { episode, days, isEnded }

  const load = useCallback(async () => {
    setLoading(true);
    const [a, e] = await Promise.all([listEpisodes("active"), listEpisodes("ended")]);
    setActive(a || []);
    setEnded(e || []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Allow the parent (Symptom Tracker view) to refresh us after it adds entries
  // that include `episodeId` (e.g. promote-flare-to-episode flow).
  useEffect(() => {
    function handler() { load(); }
    window.addEventListener("claimops:episodes-refresh", handler);
    return () => window.removeEventListener("claimops:episodes-refresh", handler);
  }, [load]);

  async function openUpdate(episode, trendHint) {
    const { days } = await getEpisode(episode.id);
    const prev = days[days.length - 1] || null;
    setUpdateState({ episode, days, prevDay: prev, initialTrend: trendHint });
  }
  async function handleUpdateSaved() {
    setUpdateState(null);
    await load();
    if (onListChanged) onListChanged();
    showToast({ variant: "success", message: "Episode updated.", duration: 2500 });
  }

  async function openEnd(episode) {
    const { days } = await getEpisode(episode.id);
    setEndState({ episode, days });
  }
  async function confirmEnd(notes) {
    await endEpisode(endState.episode.id, { notes });
    setEndState(null);
    await load();
    if (onListChanged) onListChanged();
    showToast({ variant: "success", message: "Episode ended.", duration: 2500 });
  }

  async function openTimeline(episode, isEnded = false) {
    const { days } = await getEpisode(episode.id);
    setTimelineState({ episode, days, isEnded });
  }
  async function reopenFromTimeline() {
    await reopenEpisode(timelineState.episode.id);
    setTimelineState(null);
    await load();
    if (onListChanged) onListChanged();
    showToast({ variant: "success", message: "Episode reopened — keep logging.", duration: 2500 });
  }

  const list = view === "active" ? active : ended;

  return (
    <div className="panel" data-testid="episodes-panel">
      <div className="panel-header">
        <Calendar size={16} weight="bold" />
        <span>ONGOING EPISODES</span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <button
            className={`appt-link-chip ${view === "active" ? "appt-link-chip--active" : ""}`}
            onClick={() => setView("active")}
            data-testid="episodes-tab-active"
          >
            ACTIVE ({active.length})
          </button>
          <button
            className={`appt-link-chip ${view === "ended" ? "appt-link-chip--active" : ""}`}
            onClick={() => setView("ended")}
            data-testid="episodes-tab-ended"
          >
            ENDED ({ended.length})
          </button>
          <button
            className="btn btn--accent"
            style={{ padding: "5px 10px", fontSize: 10.5 }}
            onClick={onStart}
            data-testid="start-episode-btn"
          >
            <Plus size={12} weight="bold" /> START ONGOING EPISODE
          </button>
        </div>
      </div>
      <div className="panel-body">
        {loading ? (
          <div style={{ color: "#64748B", padding: 16 }}>Loading...</div>
        ) : list.length === 0 ? (
          <div className="empty-state" data-testid={`episodes-empty-${view}`}>
            <Calendar size={28} weight="duotone" />
            <div className="empty-state-title">
              {view === "active" ? "No ongoing episodes" : "No ended episodes yet"}
            </div>
            <div className="empty-state-desc">
              {view === "active"
                ? "Use this when a flare or chronic symptom stretches across multiple days. You log it once, then add a 30-second update each day."
                : "Episodes you close out will live here so you can pull a clean multi-day VA narrative anytime."}
            </div>
          </div>
        ) : (
          <div className="episode-list">
            {list.map((ep) => (
              view === "active" ? (
                <EpisodeCard
                  key={ep.id}
                  episode={ep}
                  issues={issues}
                  onUpdate={(e, trend) => openUpdate(e, trend)}
                  onEnd={openEnd}
                  onOpen={(e) => openTimeline(e, false)}
                />
              ) : (
                <EndedEpisodeRow key={ep.id} episode={ep} onView={() => openTimeline(ep, true)} onDelete={async () => {
                  if (!window.confirm(`Delete this ended episode? (Daily entries remain in your symptom log.)`)) return;
                  await deleteEpisode(ep.id);
                  await load();
                  if (onListChanged) onListChanged();
                }} />
              )
            ))}
          </div>
        )}
      </div>

      {updateState && (
        <UpdateModal
          episode={updateState.episode}
          days={updateState.days}
          prevDay={updateState.prevDay}
          initialTrend={updateState.initialTrend}
          onCancel={() => setUpdateState(null)}
          onSaved={handleUpdateSaved}
        />
      )}
      {endState && (
        <EndSummaryModal
          episode={endState.episode}
          days={endState.days}
          onCancel={() => setEndState(null)}
          onConfirm={confirmEnd}
        />
      )}
      {timelineState && (
        <TimelineModal
          episode={timelineState.episode}
          days={timelineState.days}
          onClose={() => setTimelineState(null)}
          onEdit={() => showToast({ message: "Tap a day to edit (coming next)." })}
          onPromoteEnded={reopenFromTimeline}
          isEnded={timelineState.isEnded}
        />
      )}
    </div>
  );
}

function EndedEpisodeRow({ episode, onView, onDelete }) {
  return (
    <div className="episode-card episode-card--ended" data-testid={`episode-ended-${episode.id}`}>
      <div className="episode-card-head">
        <div className="episode-card-title">
          <Calendar size={14} weight="bold" />
          <span>{episode.symptom || "Episode"} — {episode.dayCount} day{episode.dayCount === 1 ? "" : "s"}</span>
        </div>
        <div className="episode-card-meta mono">
          ended {relativeTime(episode.endDate || episode.endedAt)}
        </div>
      </div>
      <div className="episode-card-actions">
        <button className="btn btn--outline" onClick={onView} data-testid={`ended-view-${episode.id}`}>
          <ClipboardText size={12} weight="bold" /> VIEW SUMMARY
        </button>
        <button
          className="btn btn--outline"
          onClick={onDelete}
          data-testid={`ended-delete-${episode.id}`}
          style={{ marginLeft: "auto" }}
        >
          <X size={12} weight="bold" /> DELETE
        </button>
      </div>
    </div>
  );
}
